import { Badge } from '@components/ui/badge';
import { cn } from '@lib/utils';
import type { FC, ReactNode } from 'react';

type MeiaMissionScopeCardProps = {
  code: string;
  desiredOutcome: string;
  isSelected: boolean;
  onSelect: () => void;
  /** Status visual rendered at the top-left (readiness icon on P1-P3, verdict icon on P4). */
  statusIndicator: ReactNode;
  threadCount: number;
  title: string;
};

/**
 * Canonical selectable Mission card: status indicator + code badge + thread count,
 * mission title, and the DMO excerpt. Shared by the Mission Scope strip (P1/P2/P3/P5)
 * and the P4 Scoring mission picker so the card visuals cannot drift between phases.
 */
export const MeiaMissionScopeCard: FC<MeiaMissionScopeCardProps> = (props) => {
  const { code, desiredOutcome, isSelected, onSelect, statusIndicator, threadCount, title } = props;

  return (
    <button
      className={cn(
        'flex min-w-56 max-w-72 items-start gap-2 rounded-md p-3 text-left transition-all duration-200',
        isSelected
          ? 'cursor-pointer bg-primary/15 text-primary ring-2 ring-inset ring-primary/80'
          : 'cursor-pointer bg-gray-100 text-gray-700 hover:ring-2 hover:ring-primary/80',
      )}
      onClick={onSelect}
      type="button"
    >
      {statusIndicator}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <Badge
            size="xs"
            variant="outline"
          >
            {code}
          </Badge>
          <span className="text-xs text-gray-400">
            {threadCount} {threadCount === 1 ? 'thread' : 'threads'}
          </span>
        </div>
        <span className="truncate text-xs font-semibold">{title}</span>
        <span className="line-clamp-2 text-[11px] leading-relaxed text-gray-500">
          {desiredOutcome || '(DMO not yet defined)'}
        </span>
      </div>
    </button>
  );
};
